/**
 * Subscribers Page
 * Full-featured subscriber management with TanStack Table
 */

import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { APP_PAGE_WRAPPER_CLASS } from '../lib/layout';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
  type ColumnFiltersState,
} from '@tanstack/react-table';
import toast from 'react-hot-toast';
import {
  Search,
  RefreshCw,
  User,
  Mail,
  Phone,
  MapPin,
  Package,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Send,
  CreditCard,
  PauseCircle,
  PlayCircle,
  Loader2,
  CheckCircle,
  Clock,
  AlertTriangle,
  FileText,
  ChevronDown,
  ChevronUp,
  Copy,
  Network,
} from 'lucide-react';

import {
  subscribersApi,
  type Subscriber,
  type SubscriberInvoice,
  type SubscriberPaymentReminderPreview,
  type ReminderChannelResult,
  type SendPaymentReminderResponse,
  type ListSubscribersParams,
} from '../api/subscribers.api';
import { sitesApi } from '../api/sites.api';
import { StatusIndicator } from '../components/StatusBadge';
import { PhoneDisplay } from '../components/PhoneBadge';
import { useSyncStatus } from '../hooks/useSyncStatus';
import {
  resolveDisplayUsername,
  resolveNetworkUsername,
} from '../lib/subscriberIdentity';
import { getApiErrorMessage } from '../lib/apiError';
import { InvoicePdfActions } from '../components/InvoicePdfActions';

export function SubscribersPage() {
  const queryClient = useQueryClient();

  // State
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<ListSubscribersParams['status']>();
  const [phoneValidFilter, setPhoneValidFilter] = useState<boolean | undefined>();
  const [outstandingFilter, setOutstandingFilter] = useState<boolean | undefined>();
  const [siteFilter, setSiteFilter] = useState<number | undefined>();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [selectedSubscriber, setSelectedSubscriber] = useState<Subscriber | null>(null);
  const [paymentLinksJobRunning, setPaymentLinksJobRunning] = useState(false);
  const sawPaymentLinksJobActiveRef = useRef(false);

  const { isSyncing } = useSyncStatus();

  const { data: paymentLinksJobStatus, refetch: refetchPaymentLinksJobStatus } = useQuery({
    queryKey: ['payment-order-generation-status'],
    queryFn: () => subscribersApi.getPaymentOrderGenerationStatus(),
    enabled: paymentLinksJobRunning,
    refetchInterval: paymentLinksJobRunning ? 5000 : false,
  });

  useEffect(() => {
    if (!paymentLinksJobRunning || paymentLinksJobStatus === undefined) return;

    if (paymentLinksJobStatus.isRunning) {
      sawPaymentLinksJobActiveRef.current = true;
      return;
    }

    setPaymentLinksJobRunning(false);
    queryClient.invalidateQueries({ queryKey: ['subscriber-payment-orders'] });
    queryClient.invalidateQueries({ queryKey: ['subscriber-payment-order'] });
    queryClient.invalidateQueries({ queryKey: ['subscribers'] });

    if (sawPaymentLinksJobActiveRef.current && paymentLinksJobStatus.message) {
      const r = paymentLinksJobStatus.lastResult;
      if (r && r.errors > 0) {
        toast.error(paymentLinksJobStatus.message, { duration: 8000 });
      } else {
        toast.success(paymentLinksJobStatus.message, { duration: 8000 });
      }
    }

    sawPaymentLinksJobActiveRef.current = false;
  }, [paymentLinksJobRunning, paymentLinksJobStatus, queryClient]);

  const generateLinksMutation = useMutation({
    mutationFn: () => subscribersApi.generateAllPaymentOrders(),
    onSuccess: (data) => {
      if (data.queued) {
        toast.success(data.message, { duration: 8000 });
        sawPaymentLinksJobActiveRef.current = false;
        setPaymentLinksJobRunning(true);
        refetchPaymentLinksJobStatus();
      } else if (data.reason === 'job_already_queued') {
        toast.error('Payment link generation is already running. Please wait for it to finish.', {
          duration: 6000,
        });
      } else {
        toast.success(data.message, { duration: 5000 });
      }
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Failed to generate payment links');
    },
  });

  // Fetch subscribers (refetch while sync completes)
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [
      'subscribers',
      {
        page,
        limit,
        search,
        status: statusFilter,
        phone_valid: phoneValidFilter,
        has_outstanding: outstandingFilter,
        site_id: siteFilter,
      },
    ],
    queryFn: () =>
      subscribersApi.list({
        page,
        limit,
        search: search || undefined,
        status: statusFilter,
        phone_valid: phoneValidFilter,
        has_outstanding: outstandingFilter,
        site_id: siteFilter,
      }),
    placeholderData: (previousData) => previousData,
    refetchInterval: isSyncing ? 3000 : false,
  });

  // Fetch summary stats
  const { data: summary } = useQuery({
    queryKey: ['subscribers-summary'],
    queryFn: () => subscribersApi.getSummary(),
  });

  const { data: sitesData } = useQuery({
    queryKey: ['sites'],
    queryFn: () => sitesApi.list(),
  });

  // Define columns
  const columns = useMemo<ColumnDef<Subscriber>[]>(
    () => [
      {
        accessorKey: 'fullname',
        header: 'Name',
        cell: ({ row }) => {
          const subscriber = row.original;
          return (
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-medium text-sm">
                {subscriber.fullname?.charAt(0).toUpperCase() || '?'}
              </div>
              <div>
                <div className="font-medium text-gray-900">
                  {subscriber.fullname || 'Unknown'}
                </div>
                {subscriber.subscriberCode && (
                  <div className="text-xs text-gray-500">
                    {subscriber.subscriberCode}
                  </div>
                )}
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: 'phone',
        header: 'Phone',
        cell: ({ row }) => <PhoneDisplay phone={row.original.phone} />,
      },
      {
        accessorKey: 'site',
        header: 'Site',
        cell: ({ row }) => {
          const site = row.original.site;
          return (
            <div className="text-sm">
              <div className="font-medium text-gray-900">
                {site.name || `Site ${site.crmSiteId}`}
              </div>
              <div className="text-xs text-gray-500 font-mono">ID {site.crmSiteId}</div>
            </div>
          );
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => <StatusIndicator status={row.original.status} />,
      },
      {
        accessorKey: 'package',
        header: 'Package',
        cell: ({ row }) => {
          const pkg = row.original.package;
          return pkg.name ? (
            <div className="flex items-center gap-1.5 text-sm text-gray-700">
              <Package className="w-4 h-4 text-gray-400" />
              {pkg.name}
            </div>
          ) : (
            <span className="text-gray-400 text-sm">−</span>
          );
        },
      },
      {
        accessorKey: 'zone',
        header: 'Zone/Node',
        cell: ({ row }) => {
          const { zone, node } = row.original;
          if (!zone.name && !node.name) return <span className="text-gray-400 text-sm">−</span>;
          return (
            <div className="flex flex-col text-sm">
              {zone.name && (
                <div className="flex items-center gap-1.5 text-gray-700">
                  <MapPin className="w-3.5 h-3.5 text-gray-400" />
                  {zone.name}
                </div>
              )}
              {node.name && (
                <div className="text-xs text-gray-500 ml-5">{node.name}</div>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: 'billing',
        header: 'Due / Overdue',
        cell: ({ row }) => {
          const amount = Number(
            row.original.billing.dueOverdueAmount ??
              row.original.billing.outstandingAmount ??
              0
          );
          return (
            <div className={`text-sm font-medium ${amount > 0 ? 'text-red-600' : 'text-green-600'}`}>
              ₹{amount.toFixed(2)}
            </div>
          );
        },
      },
      {
        accessorKey: 'reminderPaused',
        header: 'Reminders',
        cell: ({ row }) => {
          const paused = row.original.reminderPaused;
          const deleted = row.original.deletedInCrm;

          if (deleted) {
            return (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                Deleted
              </span>
            );
          }

          if (paused) {
            return (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                Paused
              </span>
            );
          }

          return (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
              Active
            </span>
          );
        },
      },
    ],
    []
  );

  // Table instance
  const table = useReactTable({
    data: data?.subscribers || [],
    columns,
    state: {
      sorting,
      columnFilters,
      pagination: {
        pageIndex: page - 1,
        pageSize: limit,
      },
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    pageCount: data?.pagination.totalPages || 0,
  });

  // Handle page change
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  return (
    <div className={APP_PAGE_WRAPPER_CLASS}>
      {selectedSubscriber ? (
        <SubscriberDetailDrawer
          key={selectedSubscriber.id}
          subscriber={selectedSubscriber}
          onClose={() => setSelectedSubscriber(null)}
        />
      ) : (
      <div className="p-6 min-w-0 max-w-full">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Subscribers</h1>
            <p className="text-gray-600 mt-1">
              Manage CRM subscribers and their reminder settings
            </p>
          </div>
          <button
            type="button"
            onClick={() => generateLinksMutation.mutate()}
            disabled={generateLinksMutation.isPending || paymentLinksJobRunning}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 text-sm font-medium"
          >
            {generateLinksMutation.isPending || paymentLinksJobRunning ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <CreditCard className="w-4 h-4" />
            )}
            {paymentLinksJobRunning
              ? `Generating links… (${paymentLinksJobStatus?.eligibleCount ?? '…'} left)`
              : 'Generate Payment Links'}
          </button>
        </div>

        {/* Stats */}
        {summary && (
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <div className="text-2xl font-bold text-gray-900">{summary.total}</div>
              <div className="text-sm text-gray-500">Total Subscribers</div>
            </div>
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <div className="text-2xl font-bold text-green-600">
                {summary.statusBreakdown.eligible || 0}
              </div>
              <div className="text-sm text-gray-500">Eligible</div>
            </div>
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <div className="text-2xl font-bold text-blue-600">
                {summary.phoneStats.valid}
              </div>
              <div className="text-sm text-gray-500">Valid Phones</div>
            </div>
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <div className="text-2xl font-bold text-orange-600">
                {summary.statusBreakdown.stopped || 0}
              </div>
              <div className="text-sm text-gray-500">Stopped</div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex items-center gap-4 flex-wrap min-w-0 max-w-full">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, email, phone, or code..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Site filter (CRM site ID) */}
          <select
            value={siteFilter === undefined ? '' : String(siteFilter)}
            onChange={(e) => {
              setSiteFilter(e.target.value ? Number(e.target.value) : undefined);
              setPage(1);
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 min-w-[160px]"
          >
            <option value="">All sites</option>
            {sitesData?.sites.map((site) => (
              <option key={site.id} value={site.crmSiteId}>
                {site.name} ({site.crmSiteId})
              </option>
            ))}
          </select>

          {/* Status Filter */}
          <select
            value={statusFilter || ''}
            onChange={(e) => {
              setStatusFilter((e.target.value as ListSubscribersParams['status']) || undefined);
              setPage(1);
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Statuses</option>
            <option value="eligible">Eligible</option>
            <option value="stopped">Stopped</option>
            <option value="unknown">Unknown</option>
          </select>

          {/* Phone Valid Filter */}
          <select
            value={phoneValidFilter === undefined ? '' : String(phoneValidFilter)}
            onChange={(e) => {
              const value = e.target.value;
              setPhoneValidFilter(value === '' ? undefined : value === 'true');
              setPage(1);
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Phones</option>
            <option value="true">Valid Only</option>
            <option value="false">Invalid Only</option>
          </select>

          {/* Outstanding payments filter */}
          <select
            value={outstandingFilter === undefined ? '' : String(outstandingFilter)}
            onChange={(e) => {
              const value = e.target.value;
              setOutstandingFilter(value === '' ? undefined : value === 'true');
              setPage(1);
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 min-w-[180px]"
          >
            <option value="">All outstanding</option>
            <option value="true">Has outstanding</option>
            <option value="false">No outstanding</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
        {isLoading ? (
          <div className="p-12 text-center">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400 mb-4" />
            <p className="text-gray-500">Loading subscribers...</p>
          </div>
        ) : error ? (
          <div className="p-12 text-center">
            <AlertCircle className="w-8 h-8 mx-auto text-red-400 mb-4" />
            <p className="text-red-500">Failed to load subscribers</p>
            <button
              onClick={() => refetch()}
              className="mt-4 text-blue-600 hover:text-blue-800"
            >
              Try again
            </button>
          </div>
        ) : data?.subscribers.length === 0 ? (
          <div className="p-12 text-center">
            <User className="w-8 h-8 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">No subscribers found</p>
          </div>
        ) : (
          <>
            <table className="w-full min-w-[1100px]">
              <thead className="bg-gray-50 border-b border-gray-200">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="divide-y divide-gray-200">
                {table.getRowModel().rows.map((row) => (
                  <tr
                    key={row.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => setSelectedSubscriber(row.original)}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="px-4 py-3">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {data?.pagination && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
                <div className="text-sm text-gray-500">
                  Showing {(page - 1) * limit + 1} to{' '}
                  {Math.min(page * limit, data.pagination.total)} of{' '}
                  {data.pagination.total} subscribers
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page === 1}
                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="text-sm text-gray-600">
                    Page {page} of {data.pagination.totalPages}
                  </span>
                  <button
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page >= data.pagination.totalPages}
                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      </div>
      )}
    </div>
  );
}

const DUE_STATUS_LABELS: Record<string, string> = {
  paid: 'Paid',
  due_today: 'Due Today',
  overdue: 'Overdue',
  upcoming: 'Upcoming',
  unknown: 'Unknown',
};

/** Mock phase: dry-run only; no real MSG91 / CRM writes */
const USE_MOCK_MSG91 = false;

function isUnpaidInvoice(inv: SubscriberInvoice): boolean {
  const ps = inv.status.paymentStatus;
  if (ps === 'paid' || ps === 'cancelled') return false;
  if (inv.amounts.outstanding <= 0) return false;
  return ps === 'unpaid' || ps === 'partially_paid';
}

/** Matches backend: only due_today / overdue invoices qualify for payment reminders */
function isReminderEligibleInvoice(
  inv: SubscriberInvoice,
  reminderPaused: boolean,
  deletedInCrm: boolean
): boolean {
  if (reminderPaused || deletedInCrm || inv.status.reminderPaused) return false;
  if (!isUnpaidInvoice(inv)) return false;
  const ds = inv.status.dueStatus;
  return ds === 'due_today' || ds === 'overdue';
}

function DrawerDueStatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    paid: 'bg-green-100 text-green-800 border-green-200',
    due_today: 'bg-orange-100 text-orange-800 border-orange-200',
    overdue: 'bg-red-100 text-red-800 border-red-200',
    upcoming: 'bg-blue-100 text-blue-800 border-blue-200',
    unknown: 'bg-gray-100 text-gray-600 border-gray-200',
  };
  const icons: Record<string, React.ReactNode> = {
    paid: <CheckCircle className="w-3 h-3" />,
    due_today: <Clock className="w-3 h-3" />,
    overdue: <AlertTriangle className="w-3 h-3" />,
    upcoming: <Clock className="w-3 h-3" />,
    unknown: <AlertCircle className="w-3 h-3" />,
  };
  const label = DUE_STATUS_LABELS[status] || status;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${
        styles[status] || styles.unknown
      }`}
    >
      {icons[status] || icons.unknown}
      {label}
    </span>
  );
}

function OrderStatusBadge({
  status,
  adminApprovalStatus,
}: {
  status: string;
  adminApprovalStatus?: string | null;
}) {
  if (status === 'paid' && adminApprovalStatus === 'pending') {
    return (
      <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium border bg-amber-100 text-amber-900 border-amber-200">
        Paid — Awaiting approval
      </span>
    );
  }

  const styles: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    paid: 'bg-green-100 text-green-800 border-green-200',
    failed: 'bg-red-100 text-red-800 border-red-200',
    cancelled: 'bg-gray-100 text-gray-600 border-gray-200',
    timeout: 'bg-orange-100 text-orange-800 border-orange-200',
  };
  const label =
    status === 'paid' && adminApprovalStatus === 'approved'
      ? 'Paid — Confirmed'
      : status === 'paid' && adminApprovalStatus === 'rejected'
        ? 'Paid — Rejected'
        : status;

  return (
    <span
      className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border capitalize ${
        styles[status] || 'bg-gray-100 text-gray-600 border-gray-200'
      }`}
    >
      {label}
    </span>
  );
}

function OrderScopeBadge({ scope }: { scope?: string | null }) {
  const normalized = scope === 'upcoming' ? 'upcoming' : 'payable';
  const labels = { payable: 'Payable dues', upcoming: 'Upcoming dues' };
  const styles = {
    payable: 'bg-amber-50 text-amber-800 border-amber-200',
    upcoming: 'bg-blue-50 text-blue-800 border-blue-200',
  };
  return (
    <span
      className={`inline-flex px-2 py-0.5 rounded text-[10px] font-medium border ${
        styles[normalized]
      }`}
    >
      {labels[normalized]}
    </span>
  );
}

function DrawerPaymentStatusBadge({ status }: { status: string }) {
  const labels: Record<string, string> = {
    unpaid: 'Unpaid',
    partially_paid: 'Partial',
    paid: 'Paid',
    cancelled: 'Cancelled',
    unknown: 'Unknown',
  };
  const styles: Record<string, string> = {
    unpaid: 'bg-red-50 text-red-700',
    partially_paid: 'bg-yellow-50 text-yellow-700',
    paid: 'bg-green-50 text-green-700',
    cancelled: 'bg-gray-50 text-gray-600',
    unknown: 'bg-gray-50 text-gray-500',
  };
  return (
    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${styles[status] || styles.unknown}`}>
      {labels[status] || status}
    </span>
  );
}

interface SendReminderResult {
  ok: boolean;
  status?: string;
  dryRun?: boolean;
  whatsapp?: ReminderChannelResult;
  email?: ReminderChannelResult;
  messageParams?: unknown;
  messagePreview?: SubscriberPaymentReminderPreview;
  error?: string;
}

function formatReminderSentAt(
  timestamp: string | null | undefined,
  hasContact: boolean,
  missingLabel: string
): React.ReactNode {
  if (!hasContact) {
    return <span className="italic text-gray-400">{missingLabel}</span>;
  }
  if (!timestamp) return '−';
  return new Date(timestamp).toLocaleString();
}

function buildReminderToast(data: SendPaymentReminderResponse, isMock: boolean): string {
  const prefix = isMock || data.dryRun ? 'Mock sent' : 'Sent';
  const wa = data.whatsapp?.status ?? 'skipped';
  const em = data.email?.status ?? 'skipped';
  if (wa === 'sent' && em === 'sent') return `${prefix} WhatsApp and email reminders`;
  if (wa === 'sent' && em === 'skipped') return `${prefix} WhatsApp reminder (no email)`;
  if (wa === 'skipped' && em === 'sent') return `${prefix} email reminder (no phone)`;
  if (wa === 'failed' && em === 'sent') return `${prefix} email reminder; WhatsApp failed`;
  if (wa === 'sent' && em === 'failed') return `${prefix} WhatsApp reminder; email failed`;
  if (wa === 'failed' && em === 'failed') return 'All reminder channels failed';
  return `${prefix} payment reminder`;
}

const MSG91_BODY_VARIABLE_LABELS = [
  'customer_name',
  'invoice_list',
  'total_due_amount',
  'due_date_summary',
  'payment_url',
  'payment_url_repeat',
];

function extractMsg91BodyVariables(messageParams: unknown): Array<{ label: string; value: string }> {
  if (!messageParams || typeof messageParams !== 'object') return [];
  const params = messageParams as {
    templateName?: string;
    components?: Array<{ type?: string; parameters?: Array<{ type?: string; text?: string }> }>;
  };
  const body = params.components?.find((c) => c.type === 'body');
  if (!body?.parameters?.length) return [];

  return body.parameters.map((param, index) => ({
    label: MSG91_BODY_VARIABLE_LABELS[index] ?? `variable_${index + 1}`,
    value: param.text ?? '',
  }));
}

function ChannelStatusBadge({ channel }: { channel?: ReminderChannelResult }) {
  if (!channel) {
    return <span className="text-xs font-medium text-gray-500">Unknown</span>;
  }
  const styles: Record<string, string> = {
    sent: 'text-green-600',
    skipped: 'text-gray-500',
    failed: 'text-red-600',
  };
  const labels: Record<string, string> = {
    sent: 'Sent',
    skipped: channel.skipReason === 'no_phone'
      ? 'No phone'
      : channel.skipReason === 'no_email'
        ? 'No email'
        : channel.error
          ? 'Unavailable'
          : 'Skipped',
    failed: 'Failed',
  };
  return (
    <span className={`text-xs font-medium ${styles[channel.status] || 'text-gray-500'}`}>
      {labels[channel.status] || channel.status}
    </span>
  );
}

function CopyPaymentLinkButton({ url }: { url: string }) {
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Payment link copied');
    } catch {
      toast.error('Failed to copy link');
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 text-xs font-medium"
      title={url}
    >
      <Copy className="w-3 h-3" />
      Copy Link
    </button>
  );
}

function Msg91VariableMappingTable({ messageParams }: { messageParams: unknown }) {
  const rows = extractMsg91BodyVariables(messageParams);
  if (rows.length === 0) return null;

  const templateName =
    messageParams && typeof messageParams === 'object' && 'templateName' in messageParams
      ? String((messageParams as { templateName?: string }).templateName ?? '')
      : '';

  return (
    <div className="mt-3">
      <p className="text-xs font-semibold text-gray-700 mb-2">
        MSG91 template variables
        {templateName ? (
          <span className="font-normal text-gray-500 ml-1">({templateName})</span>
        ) : null}
      </p>
      <div className="overflow-x-auto rounded border border-gray-200">
        <table className="min-w-full text-xs">
          <thead className="bg-gray-50 text-left text-gray-500">
            <tr>
              <th className="px-3 py-2 font-medium">Variable</th>
              <th className="px-3 py-2 font-medium">Value</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((row) => (
              <tr key={row.label} className="align-top">
                <td className="px-3 py-2 font-mono text-gray-600 whitespace-nowrap">{row.label}</td>
                <td className="px-3 py-2 text-gray-900 whitespace-pre-wrap break-all">{row.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function InfoRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0 py-1.5">
      <dt className="text-sm text-gray-500 leading-5">{label}</dt>
      <dd className="mt-0.5 text-sm font-medium text-gray-900 break-words leading-5">{children}</dd>
    </div>
  );
}

function KeyValueRow({
  label,
  value,
  valueClassName = 'text-sm font-medium text-gray-900',
}: {
  label: string;
  value: React.ReactNode;
  valueClassName?: string;
}) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5 min-w-0">
      <span className="text-sm text-gray-500 whitespace-nowrap">{label}</span>
      <span className={`text-right ${valueClassName}`}>{value}</span>
    </div>
  );
}

function InfoSection({
  title,
  icon,
  children,
}: {
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-3 min-w-0">
      <div className="flex items-center gap-2">
        {icon ? <span className="text-gray-400">{icon}</span> : null}
        <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
      </div>
      <dl className="grid grid-cols-1 gap-2">{children}</dl>
    </section>
  );
}

function StatusBadge({
  children,
  tone = 'neutral',
}: {
  children: React.ReactNode;
  tone?: 'neutral' | 'success' | 'warning' | 'danger' | 'info';
}) {
  const tones: Record<string, string> = {
    neutral: 'bg-gray-100 text-gray-700 border-gray-200',
    success: 'bg-green-100 text-green-700 border-green-200',
    warning: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    danger: 'bg-red-100 text-red-700 border-red-200',
    info: 'bg-blue-100 text-blue-700 border-blue-200',
  };
  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${tones[tone]}`}
    >
      {children}
    </span>
  );
}

function CustomerProfileHeader({
  subscriber,
  displayUsername,
  reminderPaused,
  totalDues,
  expiryLabel,
}: {
  subscriber: Subscriber;
  displayUsername: string;
  reminderPaused: boolean;
  totalDues: number;
  expiryLabel: string;
}) {
  const isEligible = subscriber.status.normalized === 'eligible';

  return (
    <div className="rounded-xl bg-slate-50/90 border border-slate-200/60 shadow-sm px-4 py-3 md:px-6">
      <div className="grid grid-cols-1 xl:grid-cols-[1.2fr_1fr_1.4fr] gap-4 items-center">
        <div className="min-w-0 flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-semibold text-base shrink-0">
            {subscriber.fullname?.charAt(0).toUpperCase() || '?'}
          </div>
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-gray-900 truncate">
              {subscriber.fullname || 'Unknown'}
            </h3>
            <p className="mt-0.5 text-sm font-mono text-gray-500 truncate">
              {subscriber.subscriberCode || '—'}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="min-w-0">
            <p className="text-xs text-gray-500">Username</p>
            <p className="font-medium text-gray-900 truncate">{displayUsername}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">CRM ID</p>
            <p className="font-medium text-gray-900">{subscriber.crmSubscriberId}</p>
          </div>
        </div>

        <div className="flex flex-wrap xl:justify-end items-center gap-2">
          <StatusBadge tone={reminderPaused ? 'warning' : 'success'}>
            {reminderPaused ? 'Inactive' : 'Active'}
          </StatusBadge>
          <StatusBadge tone={subscriber.billing.outstandingAmount > 0 ? 'danger' : 'success'}>
            Outstanding ₹{subscriber.billing.outstandingAmount.toFixed(2)}
          </StatusBadge>
          <StatusBadge tone={totalDues > 0 ? 'danger' : 'success'}>
            Total Dues ₹{totalDues.toFixed(2)}
          </StatusBadge>
          <StatusBadge tone="neutral">Expiry {expiryLabel}</StatusBadge>
          <StatusBadge tone={isEligible ? 'success' : 'warning'}>
            {isEligible ? 'Eligible' : 'Not eligible'}
          </StatusBadge>
        </div>
      </div>
    </div>
  );
}

function DetailCard({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl bg-white border border-slate-200/60 shadow-sm p-6 space-y-4">
      <h3 className="text-base font-medium text-gray-900">{title}</h3>
      {children}
    </div>
  );
}

function MetricCard({
  label,
  value,
  subValue,
  tone,
}: {
  label: string;
  value: React.ReactNode;
  subValue?: React.ReactNode;
  tone: 'neutral' | 'success' | 'warning' | 'danger' | 'info';
}) {
  const valueToneClass: Record<string, string> = {
    neutral: 'text-gray-900',
    success: 'text-green-700',
    warning: 'text-yellow-700',
    danger: 'text-red-700',
    info: 'text-blue-700',
  };

  return (
    <div className="rounded-lg border border-slate-200/60 bg-white px-3 py-3 min-w-[140px]">
      <p className="text-sm text-gray-500">{label}</p>
      <p className={`mt-1 text-lg font-semibold tabular-nums whitespace-nowrap ${valueToneClass[tone]}`}>
        {value}
      </p>
      {subValue ? <p className="mt-0.5 text-xs text-gray-500 whitespace-nowrap">{subValue}</p> : null}
    </div>
  );
}

function resolveDisplayEmail(
  subscriber: Subscriber,
  detail?: { email?: string | null; rawPayload?: Record<string, unknown> | null } | null
): string | null {
  const direct = (detail?.email ?? subscriber.email)?.trim();
  if (direct) return direct;

  const payload = detail?.rawPayload;
  if (!payload || typeof payload !== 'object') return null;

  for (const key of ['email', 'contact_email', 'company_email', 'email_address', 'mail']) {
    const value = payload[key];
    if (typeof value === 'string' && value.trim() && value.includes('@')) {
      return value.trim();
    }
  }
  return null;
}

/**
 * Subscriber Detail Drawer
 */
function SubscriberDetailDrawer({
  subscriber,
  onClose,
}: {
  subscriber: Subscriber;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [reminderPaused, setReminderPaused] = useState(subscriber.reminderPaused);
  const [isSending, setIsSending] = useState(false);
  const [sendResult, setSendResult] = useState<SendReminderResult | null>(null);
  const [showMockResults, setShowMockResults] = useState(false);

  const { data: appConfig } = useQuery({
    queryKey: ['app-config'],
    queryFn: () => subscribersApi.getAppConfig(),
    staleTime: 60_000,
  });

  const { data: subscriberDetail } = useQuery({
    queryKey: ['subscriber-detail', subscriber.id],
    queryFn: () => subscribersApi.get(subscriber.id),
    staleTime: 60_000,
  });

  const {
    data: invoicesData,
    isLoading: invoicesLoading,
    error: invoicesError,
    refetch: refetchInvoices,
  } = useQuery({
    queryKey: ['subscriber-invoices', subscriber.id],
    queryFn: () => subscribersApi.getInvoices(subscriber.id, { limit: 100 }),
  });

  const { data: paymentOrderSummary } = useQuery({
    queryKey: ['subscriber-payment-order', subscriber.id],
    queryFn: () => subscribersApi.getPaymentOrder(subscriber.id),
  });

  const {
    data: paymentOrdersData,
    isLoading: paymentOrdersLoading,
    refetch: refetchPaymentOrders,
  } = useQuery({
    queryKey: ['subscriber-payment-orders', subscriber.id],
    queryFn: () => subscribersApi.getPaymentOrders(subscriber.id),
  });

  const aggregateOrders = paymentOrdersData?.aggregateOrders ?? [];
  const activePaymentOrder =
    paymentOrderSummary?.paymentOrder ?? paymentOrdersData?.activePaymentOrder ?? null;
  const pendingPaymentOrder =
    paymentOrdersData?.aggregateOrders?.find(
      (o) => o.orderStatus === 'pending' && o.paymentUrl && o.crmOrderId
    ) ??
    (activePaymentOrder?.orderStatus === 'pending' &&
    activePaymentOrder.paymentUrl &&
    activePaymentOrder.crmOrderId
      ? activePaymentOrder
      : null);
  const hasActivePaymentLink =
    !!pendingPaymentOrder?.paymentUrl && !!pendingPaymentOrder?.crmOrderId;
  const hasValidPhone = !!subscriber.phone?.normalized;
  const subscriberEmail = resolveDisplayEmail(subscriber, subscriberDetail);
  const hasValidEmail = !!subscriberEmail;
  const hasContactMethod = hasValidPhone || hasValidEmail;

  const orderAwaitingApproval =
    activePaymentOrder?.orderStatus === 'paid' &&
    activePaymentOrder?.adminApprovalStatus === 'pending';

  const invoices = invoicesData?.invoices ?? [];

  const totalDues = useMemo(
    () =>
      invoices
        .filter(
          (inv) =>
            inv.status.paymentStatus !== 'paid' && inv.status.paymentStatus !== 'cancelled'
        )
        .reduce((sum, inv) => sum + inv.amounts.outstanding, 0),
    [invoices]
  );

  const unpaidCount = useMemo(
    () =>
      invoices.filter(
        (inv) =>
          inv.status.paymentStatus !== 'paid' && inv.status.paymentStatus !== 'cancelled'
      ).length,
    [invoices]
  );

  const unpaidInvoices = useMemo(
    () => invoices.filter((inv) => isUnpaidInvoice(inv)),
    [invoices]
  );

  const reminderEligibleInvoices = useMemo(
    () =>
      invoices.filter((inv) =>
        isReminderEligibleInvoice(inv, reminderPaused, subscriber.deletedInCrm)
      ),
    [invoices, reminderPaused, subscriber.deletedInCrm]
  );

  const isMockMode =
    USE_MOCK_MSG91 || appConfig?.dryRun === true || appConfig?.features?.msg91?.configured !== true;

  const pauseMutation = useMutation({
    mutationFn: () => subscribersApi.pauseReminders(subscriber.id),
    onSuccess: () => {
      setReminderPaused(true);
      toast.success('Payment reminders paused (local only)');
      queryClient.invalidateQueries({ queryKey: ['subscribers'] });
    },
    onError: () => toast.error('Failed to pause reminders'),
  });

  const resumeMutation = useMutation({
    mutationFn: () => subscribersApi.resumeReminders(subscriber.id),
    onSuccess: () => {
      setReminderPaused(false);
      toast.success('Payment reminders resumed (local only)');
      queryClient.invalidateQueries({ queryKey: ['subscribers'] });
    },
    onError: () => toast.error('Failed to resume reminders'),
  });

  const generateOrderMutation = useMutation({
    mutationFn: () => subscribersApi.generatePaymentOrder(subscriber.id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['subscriber-payment-order', subscriber.id] });
      queryClient.invalidateQueries({ queryKey: ['subscriber-payment-orders', subscriber.id] });
      if (data.action === 'created') {
        toast.success('Payment link generated');
      } else if (data.action === 'regenerated') {
        toast.success('Payment link updated for new dues');
      } else if (data.action === 'checked') {
        toast.success('Active payment link already exists');
      } else if (data.error) {
        toast.error(data.error);
      } else {
        toast.success(`Done (${data.action})`);
      }
    },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Failed to generate payment link')),
  });

  const reviewPaymentMutation = useMutation({
    mutationFn: ({ orderId, approved }: { orderId: number; approved: boolean }) =>
      subscribersApi.reviewPaymentOrder(subscriber.id, orderId, approved),
    onSuccess: (data) => {
      toast.success(data.message);
      queryClient.invalidateQueries({ queryKey: ['subscriber-payment-order', subscriber.id] });
      queryClient.invalidateQueries({ queryKey: ['subscriber-payment-orders', subscriber.id] });
      queryClient.invalidateQueries({ queryKey: ['subscriber-invoices', subscriber.id] });
      queryClient.invalidateQueries({ queryKey: ['subscribers'] });
      refetchPaymentOrders();
      refetchInvoices();
    },
    onError: (err: Error) => toast.error(err.message || 'Failed to review payment'),
  });

  const handleSendReminders = useCallback(async () => {
    if (reminderEligibleInvoices.length === 0 || !hasActivePaymentLink || !hasContactMethod) return;
    setIsSending(true);
    setSendResult(null);

    try {
      const data = await subscribersApi.sendPaymentReminder(subscriber.id, USE_MOCK_MSG91);
      setSendResult({
        ok: true,
        status: data.status,
        dryRun: data.dryRun,
        whatsapp: data.whatsapp,
        email: data.email,
        messageParams: data.messageParams,
        messagePreview: data.messagePreview,
      });
      setShowMockResults(true);
      queryClient.invalidateQueries({ queryKey: ['subscriber-payment-order', subscriber.id] });
      queryClient.invalidateQueries({ queryKey: ['subscriber-payment-orders', subscriber.id] });
      toast.success(buildReminderToast(data, isMockMode));
    } catch (err) {
      const message = getApiErrorMessage(err, 'Failed to send payment reminder');
      setSendResult({ ok: false, error: message });
      setShowMockResults(true);
      toast.error(message);
    } finally {
      setIsSending(false);
    }
  }, [
    reminderEligibleInvoices.length,
    hasActivePaymentLink,
    hasContactMethod,
    subscriber.id,
    isMockMode,
    queryClient,
  ]);

  const displayUsername = resolveDisplayUsername({
    ...subscriber,
    ...subscriberDetail,
  });
  const networkUsername = resolveNetworkUsername({
    ...subscriber,
    ...subscriberDetail,
  });

  const expiryLabel = subscriber.billing.expiryDate
    ? new Date(subscriber.billing.expiryDate).toLocaleDateString()
    : '−';

  const dueLabel = subscriber.billing.dueDate
    ? new Date(subscriber.billing.dueDate).toLocaleDateString()
    : '−';

  const crmSnapshotDiff =
    Math.abs(
      (subscriber.billing.crmOutstandingAmount ?? subscriber.billing.outstandingAmount) - totalDues
    ) > 0.01;

  return (
    <div className="p-6 min-w-0 max-w-full bg-gray-50">
      <div className="w-full min-w-0">
        {/* Page chrome */}
        <div className="flex items-center mb-4">
          <button
            onClick={onClose}
            className="inline-flex items-center gap-1.5 px-2 py-1.5 -ml-2 rounded-md text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-200 transition-colors"
            type="button"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to Subscribers
          </button>
        </div>

        <div className="mb-4">
          <CustomerProfileHeader
            subscriber={subscriber}
            displayUsername={displayUsername}
            reminderPaused={reminderPaused}
            totalDues={totalDues}
            expiryLabel={expiryLabel}
          />
        </div>

        {/* Details layout */}
        <div className="grid grid-cols-1 xl:grid-cols-[2.1fr_1.3fr] gap-6 mb-6 items-start">
          <div>
            <DetailCard title="Customer Information">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <InfoSection
                  title="Contact Information"
                  icon={<Mail className="w-4 h-4" />}
                >
                  <InfoRow label="Email">{subscriberEmail || '−'}</InfoRow>
                  <InfoRow label="Phone">
                    <span className="inline-flex items-center gap-1">
                      <Phone className="w-3.5 h-3.5 text-gray-400" />
                      {subscriber.phone.formatted}
                    </span>
                    {!subscriber.phone.valid && subscriber.phone.validationError && (
                      <p className="text-xs text-red-500 mt-0.5 font-normal">
                        {subscriber.phone.validationError}
                      </p>
                    )}
                  </InfoRow>
                </InfoSection>

                <InfoSection
                  title="CRM Information"
                  icon={<FileText className="w-4 h-4 text-gray-400" />}
                >
                  <InfoRow label="CRM ID">{subscriber.crmSubscriberId}</InfoRow>
                  <InfoRow label="Subscriber Code">{subscriber.subscriberCode || '−'}</InfoRow>
                  <InfoRow label="Package">{subscriber.package.name || '−'}</InfoRow>
                  <InfoRow label="Node">{subscriber.node.name || '−'}</InfoRow>
                  <InfoRow label="Zone">{subscriber.zone.name || '−'}</InfoRow>
                </InfoSection>

                <InfoSection
                  title="Network Details"
                  icon={<Network className="w-4 h-4 text-gray-400" />}
                >
                  <InfoRow label="Username">{displayUsername}</InfoRow>
                  <InfoRow label="Billing Profile">{subscriber.billing.profile || '−'}</InfoRow>
                  <InfoRow label="Site">
                    {subscriber.site.name || `Site ${subscriber.site.crmSiteId}`}
                  </InfoRow>
                  <InfoRow label="CRM Site ID">{subscriber.site.crmSiteId}</InfoRow>
                  {networkUsername && <InfoRow label="MAC / PPPoE">{networkUsername}</InfoRow>}
                </InfoSection>
              </div>
            </DetailCard>
          </div>

          <div>
            <DetailCard title="Subscription Details">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 pb-4 border-b border-slate-200/60">
                <KeyValueRow label="Due Date" value={dueLabel} />
                <KeyValueRow label="Expiry Date" value={expiryLabel} />
                <KeyValueRow
                  label="Reminder Status"
                  value={
                    <StatusBadge tone={reminderPaused ? 'warning' : 'success'}>
                      {reminderPaused ? 'Paused' : 'Active'}
                    </StatusBadge>
                  }
                />
              </div>
              <div className="pt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <MetricCard
                  label="Outstanding"
                  value={`₹${subscriber.billing.outstandingAmount.toFixed(2)}`}
                  tone={subscriber.billing.outstandingAmount > 0 ? 'danger' : 'success'}
                />
                <MetricCard
                  label="Total Dues"
                  value={`₹${totalDues.toFixed(2)}`}
                  tone={totalDues > 0 ? 'danger' : 'success'}
                />
                <MetricCard
                  label="Active Invoices"
                  value={unpaidCount}
                  subValue={`${invoices.length} synced`}
                  tone={unpaidCount > 0 ? 'info' : 'neutral'}
                />
              </div>
              {crmSnapshotDiff && (
                <div className="pt-3 text-xs text-gray-500">
                  CRM snapshot: ₹{(subscriber.billing.crmOutstandingAmount ?? 0).toFixed(2)}
                </div>
              )}
            </DetailCard>
          </div>
        </div>

        {/* Alerts & operational section */}
        <div className="space-y-4">
          {isMockMode && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-xs">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>
                Mock MSG91 — WhatsApp and email reminders are simulated locally. No messages are
                sent and the CRM is not updated.
              </span>
            </div>
          )}

          {/* Invoices */}
          <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
              <FileText className="w-4 h-4 text-gray-400" />
              <h4 className="text-sm font-semibold text-gray-900">Invoices</h4>
            </div>
            <div className="p-4 pt-3">
              {invoicesLoading && (
                <div className="py-6 text-center text-gray-500 text-sm">
                  <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
                  Loading invoices…
                </div>
              )}
              {invoicesError && (
                <p className="text-sm text-red-600 py-2">Failed to load invoices.</p>
              )}
              {!invoicesLoading && !invoicesError && invoices.length === 0 && (
                <p className="text-sm text-gray-500 py-2">
                  No invoices synced for this subscriber. Run CRM sync if you expect data.
                </p>
              )}
              {!invoicesLoading && invoices.length > 0 && (
                <div className="border border-gray-200 rounded-md overflow-auto max-h-[320px]">
                  <table className="min-w-full text-xs">
                    <thead className="bg-gray-50 text-left text-[11px] text-gray-500 uppercase sticky top-0 z-10 shadow-[0_1px_0_0_rgb(229,231,235)]">
                      <tr>
                        <th className="px-3 py-2 font-semibold">Invoice</th>
                        <th className="px-3 py-2 font-semibold">Due</th>
                        <th className="px-3 py-2 font-semibold text-right">Outstanding</th>
                        <th className="px-3 py-2 font-semibold">Payment</th>
                        <th className="px-3 py-2 font-semibold">Due status</th>
                        <th className="px-3 py-2 font-semibold text-center">PDF</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {invoices.map((inv) => (
                        <tr key={inv.id} className="hover:bg-gray-50/80">
                          <td className="px-3 py-1.5 font-medium text-gray-900">
                            {inv.invoiceNumber || `#${inv.crmInvoiceId}`}
                          </td>
                          <td className="px-3 py-1.5 text-gray-600 whitespace-nowrap">
                            {inv.dates.dueDate
                              ? new Date(inv.dates.dueDate).toLocaleDateString()
                              : '−'}
                          </td>
                          <td className="px-3 py-1.5 text-right font-medium text-gray-900">
                            ₹{inv.amounts.outstanding.toFixed(2)}
                          </td>
                          <td className="px-3 py-1.5">
                            <DrawerPaymentStatusBadge status={inv.status.paymentStatus} />
                          </td>
                          <td className="px-3 py-1.5">
                            <DrawerDueStatusBadge status={inv.status.dueStatus} />
                          </td>
                          <td className="px-3 py-1.5 text-center">
                            <InvoicePdfActions
                              invoiceId={inv.localInvoiceId ?? undefined}
                              pdfUrl={inv.pdf_url}
                              hasPdf={inv.has_pdf}
                              compact
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Payment Links & Status */}
          <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <h4 className="text-sm font-semibold text-gray-900">Payment Links &amp; Status</h4>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => generateOrderMutation.mutate()}
                  disabled={
                    generateOrderMutation.isPending ||
                    unpaidInvoices.length === 0 ||
                    reminderPaused ||
                    subscriber.deletedInCrm ||
                    orderAwaitingApproval
                  }
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 text-xs font-medium"
                >
                  {generateOrderMutation.isPending ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <CreditCard className="w-3 h-3" />
                  )}
                  Generate Link
                </button>
                <button
                  type="button"
                  onClick={handleSendReminders}
                  disabled={
                    isSending ||
                    reminderPaused ||
                    subscriber.deletedInCrm ||
                    orderAwaitingApproval ||
                    !hasContactMethod ||
                    reminderEligibleInvoices.length === 0 ||
                    !hasActivePaymentLink
                  }
                  title={
                    orderAwaitingApproval
                      ? 'Payment awaiting admin approval'
                      : !hasContactMethod
                        ? 'Subscriber has no valid phone number or email'
                        : !hasActivePaymentLink
                          ? 'No active payment link — generate one first'
                          : reminderEligibleInvoices.length === 0
                            ? 'No due or overdue invoices'
                            : reminderPaused
                              ? 'Reminders are paused'
                              : undefined
                  }
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium"
                >
                  {isSending ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Send className="w-3 h-3" />
                  )}
                  {isSending ? 'Sending…' : 'Send Payment Reminder'}
                </button>
                {!subscriber.deletedInCrm &&
                  (reminderPaused ? (
                    <button
                      type="button"
                      onClick={() => resumeMutation.mutate()}
                      disabled={resumeMutation.isPending}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-xs font-medium"
                    >
                      <PlayCircle className="w-3 h-3" />
                      Resume Payment Reminder
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => pauseMutation.mutate()}
                      disabled={pauseMutation.isPending}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50 text-xs font-medium"
                    >
                      <PauseCircle className="w-3 h-3" />
                      Pause Payment Reminder
                    </button>
                  ))}
              </div>
            </div>

            {paymentOrdersLoading && (
              <div className="flex items-center gap-2 text-gray-500 text-sm">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading payment links…
              </div>
            )}

            {!paymentOrdersLoading && (
              <>
                {orderAwaitingApproval && activePaymentOrder && (
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-3 py-2 rounded-md border border-amber-300 bg-amber-50">
                    <div className="flex items-start gap-2 min-w-0">
                      <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                      <p className="text-xs text-amber-900">
                        <span className="font-semibold">Payment received — admin approval required.</span>{' '}
                        Gateway reports ₹{activePaymentOrder.totalAmount.toFixed(2)} paid for order{' '}
                        <span className="font-mono">{activePaymentOrder.crmOrderId}</span>.
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={() =>
                          reviewPaymentMutation.mutate({
                            orderId: activePaymentOrder.id,
                            approved: true,
                          })
                        }
                        disabled={reviewPaymentMutation.isPending}
                        className="flex items-center gap-1 px-2.5 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 text-xs font-medium"
                      >
                        {reviewPaymentMutation.isPending ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <CheckCircle className="w-3 h-3" />
                        )}
                        Yes — payment received (clear dues)
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          reviewPaymentMutation.mutate({
                            orderId: activePaymentOrder.id,
                            approved: false,
                          })
                        }
                        disabled={reviewPaymentMutation.isPending}
                        className="flex items-center gap-1 px-2.5 py-1 bg-white border border-red-300 text-red-700 rounded-md hover:bg-red-50 disabled:opacity-50 text-xs font-medium"
                      >
                        No — not paid (generate new link)
                      </button>
                    </div>
                  </div>
                )}

                <div>
                  <h5 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-2">
                    Payment orders (payable &amp; upcoming)
                  </h5>
                  {aggregateOrders.length === 0 ? (
                    <p className="text-gray-600 text-xs">No payment orders yet.</p>
                  ) : (
                    <div className="border border-gray-200 rounded-md overflow-auto max-h-[280px]">
                      <table className="min-w-full text-xs">
                        <thead className="bg-gray-50 text-left text-[11px] text-gray-500 uppercase sticky top-0 z-10 shadow-[0_1px_0_0_rgb(229,231,235)]">
                          <tr>
                            <th className="px-3 py-2 font-semibold">Created</th>
                            <th className="px-3 py-2 font-semibold">Scope</th>
                            <th className="px-3 py-2 font-semibold">Status</th>
                            <th className="px-3 py-2 font-semibold">Order ID</th>
                            <th className="px-3 py-2 font-semibold text-right">Amount</th>
                            <th className="px-3 py-2 font-semibold">Invoices</th>
                            <th className="px-3 py-2 font-semibold">Last WhatsApp Reminder</th>
                            <th className="px-3 py-2 font-semibold">Last Email Reminder</th>
                            <th className="px-3 py-2 font-semibold">Link</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 bg-white">
                          {aggregateOrders.map((order) => (
                            <tr key={order.id} className="hover:bg-gray-50/80">
                              <td className="px-3 py-1.5 whitespace-nowrap text-gray-600">
                                {new Date(order.createdAt).toLocaleString()}
                              </td>
                              <td className="px-3 py-1.5">
                                <OrderScopeBadge scope={order.orderScope} />
                              </td>
                              <td className="px-3 py-1.5">
                                <OrderStatusBadge
                                  status={order.orderStatus}
                                  adminApprovalStatus={order.adminApprovalStatus}
                                />
                              </td>
                              <td className="px-3 py-1.5 font-mono text-gray-700">
                                {order.crmOrderId || '−'}
                              </td>
                              <td className="px-3 py-1.5 text-right font-medium">
                                ₹{order.totalAmount.toFixed(2)}
                              </td>
                              <td className="px-3 py-1.5 text-gray-600">
                                {order.unpaidInvoiceCount}
                              </td>
                              <td className="px-3 py-1.5 whitespace-nowrap text-gray-600">
                                {formatReminderSentAt(
                                  order.whatsappLastSentAt,
                                  hasValidPhone,
                                  'No phone'
                                )}
                              </td>
                              <td className="px-3 py-1.5 whitespace-nowrap text-gray-600">
                                {formatReminderSentAt(
                                  order.emailLastSentAt,
                                  hasValidEmail,
                                  'No email'
                                )}
                              </td>
                              <td className="px-3 py-1.5 max-w-[160px]">
                                {order.orderStatus === 'pending' && order.paymentUrl ? (
                                  <CopyPaymentLinkButton url={order.paymentUrl} />
                                ) : (
                                  '−'
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {sendResult && (
            <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
              <button
                type="button"
                onClick={() => setShowMockResults((v) => !v)}
                className="w-full flex items-center justify-between px-4 py-2 bg-gray-50 text-sm font-medium text-gray-700 hover:bg-gray-100"
              >
                {isMockMode ? 'Mock send result' : 'Send result'}
                {showMockResults ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              {showMockResults && (
                <div className="px-4 py-3 text-sm border-t border-gray-100 space-y-4">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium">Subscriber payment reminder</span>
                    <span
                      className={
                        sendResult.ok ? 'text-green-600 text-xs font-medium' : 'text-red-600 text-xs font-medium'
                      }
                    >
                      {sendResult.ok ? sendResult.status || 'ok' : sendResult.error}
                    </span>
                  </div>

                  {sendResult.ok && sendResult.whatsapp && (
                    <div className="rounded border border-gray-200 p-3 space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                          WhatsApp
                        </span>
                        <ChannelStatusBadge channel={sendResult.whatsapp} />
                      </div>
                      {sendResult.whatsapp?.status === 'skipped' && sendResult.whatsapp.skipReason === 'no_phone' && (
                        <p className="text-xs text-gray-500 italic">No phone number on file</p>
                      )}
                      {sendResult.whatsapp?.status === 'failed' && sendResult.whatsapp.error && (
                        <p className="text-xs text-red-600">{sendResult.whatsapp.error}</p>
                      )}
                      {sendResult.whatsapp?.status === 'sent' && sendResult.messagePreview && (
                        <div className="rounded border border-gray-100 bg-gray-50 p-2 whitespace-pre-wrap text-xs text-gray-800">
                          {sendResult.messagePreview.messageText}
                        </div>
                      )}
                      {sendResult.whatsapp?.status === 'sent' && sendResult.messageParams != null && (
                        <Msg91VariableMappingTable messageParams={sendResult.messageParams} />
                      )}
                    </div>
                  )}

                  {sendResult.ok && sendResult.email && (
                    <div className="rounded border border-gray-200 p-3 space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                          Email
                        </span>
                        <ChannelStatusBadge channel={sendResult.email} />
                      </div>
                      {sendResult.email?.status === 'skipped' && sendResult.email.skipReason === 'no_email' && (
                        <p className="text-xs text-gray-500 italic">No email on file</p>
                      )}
                      {sendResult.email?.status === 'skipped' && !sendResult.email.skipReason && sendResult.email.error && (
                        <p className="text-xs text-amber-700">{sendResult.email.error}</p>
                      )}
                      {sendResult.email?.status === 'failed' && sendResult.email.error && (
                        <p className="text-xs text-red-600">{sendResult.email.error}</p>
                      )}
                      {sendResult.email?.status === 'sent' && sendResult.email.emailPreview && (
                        <div className="space-y-1 text-xs">
                          <p>
                            <span className="text-gray-500">To:</span>{' '}
                            {sendResult.email.emailPreview.to}
                          </p>
                          <p>
                            <span className="text-gray-500">Subject:</span>{' '}
                            {sendResult.email.emailPreview.subject}
                          </p>
                          <div className="rounded border border-gray-100 bg-gray-50 p-2 whitespace-pre-wrap text-gray-800 mt-1">
                            {sendResult.email.emailPreview.body}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {sendResult.ok && sendResult.messagePreview && (
                    <div className="space-y-3">
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-xs">
                          <thead>
                            <tr className="text-left text-gray-500 border-b border-gray-200">
                              <th className="py-1 pr-3 font-medium">Invoice</th>
                              <th className="py-1 pr-3 font-medium">Amount</th>
                              <th className="py-1 pr-3 font-medium">Due date</th>
                              <th className="py-1 font-medium">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {sendResult.messagePreview.invoices.map((inv) => (
                              <tr key={inv.invoiceNumber} className="border-b border-gray-100 last:border-0">
                                <td className="py-1.5 pr-3 font-medium text-gray-900">{inv.invoiceNumber}</td>
                                <td className="py-1.5 pr-3">₹{inv.amount.toFixed(2)}</td>
                                <td className="py-1.5 pr-3">{inv.dueDate ?? '—'}</td>
                                <td className="py-1.5 capitalize">{inv.dueStatus.replace('_', ' ')}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr>
                              <td className="pt-2 pr-3 font-semibold text-gray-900">Total due</td>
                              <td className="pt-2 pr-3 font-semibold text-gray-900" colSpan={3}>
                                ₹{sendResult.messagePreview.totalDue.toFixed(2)}
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-600">
                        <span className="shrink-0">Payment link:</span>
                        <span className="truncate text-gray-800">{sendResult.messagePreview.paymentUrl}</span>
                        <CopyPaymentLinkButton url={sendResult.messagePreview.paymentUrl} />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
